import { DropDown, Form, Modal } from '@finbeat/react-toolkit';
import { Dictionary } from 'lodash';
import {
    MerchantOrderAdd,
    MerchantOrderAddDTO,
    MerchantOrderProductAdd,
    MerchantTradeOrgContact,
} from 'models/api/merchant';
import { MerchantOrderProvider } from 'providers/MerchantOrderProvider';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { UseQueryResult } from 'react-query';
import { useMerchantWorkplaceState } from '../Merchant';
import '../Merchant.scss';
import MerchantOrderForm from './MerchantOrderForm';
import { useTranslation } from 'react-i18next';
import { numberFromLocaleString } from 'utils/number';

export type MerchantOrderModalFormType = MerchantOrderAdd & {
    isEditable: boolean;
};

type MerchantOrderModalProps = {
    isOpen?: boolean;
    toggle: Dispatch<SetStateAction<boolean>>;
    contacts: MerchantTradeOrgContact[];
    getContactsQuery: UseQueryResult<MerchantTradeOrgContact[], unknown>;
    tariffGroups: DropDown[];
    tariffGroupsFetching: boolean;
    settings: Dictionary<string> | undefined;
    settingsFetching: boolean;
};

const initialItem: MerchantOrderModalFormType = {
    isMobileApp: false,
    phoneNumber: '',
    products: [
        {
            productName: '',
            productCost: '0',
            productCnt: null,
        },
    ],
    tariffId: null,
    isEditable: true,
};

const MerchantOrderModal = ({
    isOpen = false,
    toggle,
    contacts,
    getContactsQuery,
    tariffGroups,
    tariffGroupsFetching,
    settings,
    settingsFetching,
}: MerchantOrderModalProps) => {
    const { currentItem, addItemMutation } = useMerchantWorkplaceState();
    const { i18n } = useTranslation();
    const isEditable = currentItem == null;

    const defaultValues = useMemo(
        () => ({
            ...initialItem,
            ...currentItem,
            products: currentItem?.products?.map(
                (p): MerchantOrderProductAdd => ({
                    productCost: p.productCostWithoutTariff.toLocaleString(i18n.language),
                    productCnt: p.productCnt,
                    productName: p.productName,
                })
            ) ?? [
                {
                    productName: '',
                    productCost: '0',
                    productCnt: null,
                },
            ],
            isEditable: isEditable,
            isMobileApp: currentItem?.source === 'mobile',
        }),
        [currentItem, isEditable]
    );

    const saveChanges = (data: MerchantOrderModalFormType) => {
        const order: MerchantOrderAddDTO = {
            ...data,
            products: data.products.map((product) => ({
                ...product,
                productCost: numberFromLocaleString(product.productCost ?? '0', i18n.language),
            })),
        };
        addItemMutation.mutate(order);
        toggle(false);
    };

    return (
        <Modal
            size="lg"
            isOpen={isOpen}
            toggle={() => toggle(false)}
            scrollable
            className="merchant__modal"
        >
            <Form<MerchantOrderModalFormType>
                onSubmit={(data) => saveChanges(data)}
                defaultValues={defaultValues}
                className="form-in-modal"
                formProps={{ mode: 'all' }}
            >
                <MerchantOrderProvider>
                    <MerchantOrderForm
                        contacts={contacts}
                        getContactsQuery={getContactsQuery}
                        settings={settings}
                        settingsFetching={settingsFetching}
                        tariffGroups={tariffGroups}
                        tariffGroupsFetching={tariffGroupsFetching}
                        toggle={toggle}
                    />
                </MerchantOrderProvider>
            </Form>
        </Modal>
    );
};

export default MerchantOrderModal;
